/**
 * One event: GET /v1/events/{id} (slug or uuid) for the card, its media and its fight card,
 * plus GET /v1/events/{id}/stats for per-bout totals. Stats are optional: a failure there
 * leaves the fight card without stat lines.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, unwrap } from '../../api';
import { imageUrl, imageSrcSet } from '../../api/image';
import { resolvePath } from '../../api/resolve';
import LoadingSpinner from '../common/LoadingSpinner';
import WatchButton from '../common/WatchButton';
import WatchOn from '../common/WatchOn';
import BoutList from './BoutList';
import { formatEventDate } from './EventsPage';
import './EventsPage.css';

const watchPath = m => `/watch/${m.slug || m.id}`;

const Message = ({ title, text }) => (
  <div className="min-h-screen bg-primary text-white flex items-center justify-center p-6">
    <div className="text-center">
      <h2 className="mb-2 text-2xl font-display uppercase">{title}</h2>
      <p className="mb-6 text-white/70">{text}</p>
      <Link to="/events" className="text-accent">
        Back to Events
      </Link>
    </div>
  </div>
);

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setEvent(null);
    setStats([]);
    setError(null);
    const path = { params: { path: { id } } };
    unwrap(api.GET('/v1/events/{id}', path))
      .then(body => {
        if (!active) {
          return;
        }
        // Loaded under a legacy Webflow id: the address bar takes the slug.
        if (body.data.slug && body.data.slug !== id) {
          navigate(`/events/${body.data.slug}`, { replace: true });
          return;
        }
        setEvent(body.data);
      })
      .catch(async err => {
        const canonical = err?.code === 'NOT_FOUND' && (await resolvePath(`/events/${id}`));
        if (!active) {
          return;
        }
        if (canonical && canonical !== `/events/${id}`) {
          navigate(canonical, { replace: true });
          return;
        }
        setError(err);
      });
    unwrap(api.GET('/v1/events/{id}/stats', path))
      .then(body => active && setStats(body.data?.bouts || []))
      .catch(err => console.warn('EventDetailPage: no stats:', err?.message));
    return () => {
      active = false;
    };
  }, [id, navigate]);

  if (error?.code === 'NOT_FOUND') {
    return <Message title="Event not found" text="We couldn't find this event." />;
  }
  if (error?.code === 'NOT_PUBLISHED') {
    return <Message title="Not available yet" text="This event isn't available yet." />;
  }
  if (error) {
    return <Message title="Something went wrong" text="We couldn't load this event right now." />;
  }
  if (!event) {
    return <LoadingSpinner message="Loading event..." />;
  }

  const asset = event.art?.landscape;
  const venue = [event.venue_name, event.location].filter(Boolean).join(' · ');
  const mediaSections = Object.entries(event.media || {}).filter(([, items]) => items.length > 0);

  return (
    <div className="min-h-screen bg-primary text-white">
      <div className="events-hero">
        <div className="events-hero-bg">
          {asset && (
            <img
              src={imageUrl(asset, '16x9', 1920)}
              srcSet={imageSrcSet(asset, '16x9', 2560)}
              sizes="100vw"
              alt={event.name}
              className="events-hero-bg-image"
            />
          )}
          <div className="events-hero-overlay" />
        </div>
        <div className="events-hero-content">
          <h1 className="events-hero-title">{event.name}</h1>
          <div className="events-hero-meta">
            {event.starts_at && (
              <div className="events-hero-meta-item">
                <span>{formatEventDate(event.starts_at)}</span>
              </div>
            )}
            {venue && (
              <div className="events-hero-meta-item">
                <span>{venue}</span>
              </div>
            )}
          </div>
          <div className="events-hero-actions">
            {event.primary_media ? (
              <WatchButton
                item={event.primary_media}
                onPlay={() => navigate(watchPath(event.primary_media))}
              />
            ) : (
              event.watch_on && <WatchOn watchOn={event.watch_on} />
            )}
            {event.buy_tickets_url && (
              <a
                href={event.buy_tickets_url}
                target="_blank"
                rel="noopener noreferrer"
                className="events-hero-btn-secondary"
              >
                Buy Tickets
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto pb-16 events-content">
        {event.bouts?.length > 0 && (
          <section className="events-section">
            <h2 className="events-section-header__title">Fight Card</h2>
            <BoutList bouts={event.bouts} stats={stats} />
          </section>
        )}

        {mediaSections.map(([kind, items]) => (
          <section key={kind} className="events-section">
            {/* Every ref of a group shares its kind, so its CMS label heads the section. */}
            <h2 className="events-section-header__title">{items[0].kind_label}</h2>
            <ul className="mt-2 space-y-1">
              {items.map(m => (
                <li key={m.id} className="flex items-center gap-3">
                  <Link to={watchPath(m)} className="text-accent hover:underline uppercase">
                    {m.title}
                  </Link>
                  {m.subtitle && <span className="text-sm text-white/60">{m.subtitle}</span>}
                  {/* Airs on a partner: link out from here, no fetch per media. */}
                  {m.watch_on && <WatchOn watchOn={m.watch_on} className="text-sm text-white/80" />}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

export default EventDetailPage;
